import { Chapter, Page } from '@fsp/types';
import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { useParams, Outlet } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from './AuthContext';
import { useChapter as useChapterQuery, chapterKeys } from '../hooks/queries/useChapters';

interface ChapterContextType {
  // Chapter data
  chapter: Chapter | null;
  pages: Page[];
  loading: boolean;
  error: string | null;

  // Navigation helpers
  getPreviousPage: (currentPageId: string) => Page | null;
  getNextPage: (currentPageId: string) => Page | null;

  // Refresh
  refreshChapter: () => void;
}

const ChapterContext = createContext<ChapterContextType | undefined>(undefined);

export const ChapterProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { organisationId } = useAuth();
  const { planId, chapterId } = useParams<{
    planId: string;
    chapterId: string;
  }>();
  const queryClient = useQueryClient();

  // Use React Query hook
  const {
    data: chapterData,
    isLoading: loading,
    error: queryError,
  } = useChapterQuery(organisationId || undefined, planId || undefined, chapterId || undefined);

  const chapter = chapterData?.chapter || null;
  const pages = useMemo(() => {
    if (!chapterData?.pages) return [];
    return [...chapterData.pages].sort((a, b) => {
      // Pages without a sort order should come after those with one
      if (a.sortOrder == null && b.sortOrder == null) return 0;
      if (a.sortOrder == null) return 1;
      if (b.sortOrder == null) return -1;
      return a.sortOrder - b.sortOrder;
    });
  }, [chapterData?.pages]);

  const refreshChapter = () => {
    if (organisationId && planId && chapterId) {
      // Invalidate the chapter detail
      queryClient.invalidateQueries({
        queryKey: chapterKeys.detail(organisationId, planId, chapterId),
      });
      // Also invalidate the plan's chapter list to keep it in sync
      queryClient.invalidateQueries({
        queryKey: chapterKeys.list(organisationId, planId),
      });
    }
  };

  // Navigation helpers
  const getPreviousPage = useCallback(
    (currentPageId: string): Page | null => {
      const currentIndex = pages.findIndex((p) => p.id === currentPageId);
      if (currentIndex <= 0) return null;
      return pages[currentIndex - 1];
    },
    [pages]
  );

  const getNextPage = useCallback(
    (currentPageId: string): Page | null => {
      const currentIndex = pages.findIndex((p) => p.id === currentPageId);
      if (currentIndex === -1 || currentIndex >= pages.length - 1) return null;
      return pages[currentIndex + 1];
    },
    [pages]
  );

  const value = useMemo(
    () => ({
      chapter,
      pages,
      loading,
      error: queryError?.message || null,
      getPreviousPage,
      getNextPage,
      refreshChapter,
    }),
    [chapter, pages, loading, queryError, getPreviousPage, getNextPage]
  );

  return (
    <ChapterContext.Provider value={value}>
      {children || <Outlet />}
    </ChapterContext.Provider>
  );
};

export const useChapter = (): ChapterContextType => {
  const context = useContext(ChapterContext);
  if (!context) {
    throw new Error('useChapter must be used within a ChapterProvider');
  }
  return context;
};
